import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Badge, Button } from 'flowbite-react';
import { HiEye, HiDownload, HiPlus } from 'react-icons/hi';
import { leasApi, getUserErrorMessage } from '../api';
import { DataTable, type ColumnDef } from '../components/tables';
import { Loading } from '../components/common/Loading';
import { CreateLEAModal } from '../components/modals/CreateLEAModal';
import { OrganizationLogo } from '../components/organization/OrganizationLogo';
import { useAuth } from '../contexts/AuthContext';
import type { LEAListItem } from '../types/api';

export const OrganizationsPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'superAdmin';
  const userOrgIds = useMemo(
    () => new Set(currentUser?.organizationIds ?? []),
    [currentUser?.organizationIds]
  );
  const [leas, setLeas] = useState<LEAListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadLEAs();
  }, []);

  const loadLEAs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await leasApi.list();
      setLeas(data);
    } catch (err: any) {
      setError(getUserErrorMessage(err, 'Failed to load organizations'));
      console.error('Failed to load LEAs:', err);
    } finally {
      setLoading(false);
    }
  };

  // Default ordering: float the current user's associated organization(s) to
  // the top, with the rest alphabetized below. Clicking a column header
  // applies a normal sort that overrides this initial ordering.
  const displayLeas = useMemo(() => {
    if (userOrgIds.size === 0) return leas;
    const byName = (a: LEAListItem, b: LEAListItem) =>
      a.nameOfInstitution.localeCompare(b.nameOfInstitution);
    const own: LEAListItem[] = [];
    const others: LEAListItem[] = [];
    for (const lea of leas) {
      if (userOrgIds.has(lea.educationOrganizationId)) own.push(lea);
      else others.push(lea);
    }
    return [...own.sort(byName), ...others.sort(byName)];
  }, [leas, userOrgIds]);

  // Define table columns
  const columns: ColumnDef<LEAListItem>[] = useMemo(() => [
    {
      id: 'nameOfInstitution',
      header: 'Organization Name',
      accessor: (row) => `${row.nameOfInstitution} ${row.shortNameOfInstitution || ''} ${row.schoolNames?.join(' ') || ''}`,
      sortable: true,
      searchable: true,
      render: (_value, row) => (
        <div className="flex items-center gap-3">
          <OrganizationLogo leaId={row.leaId} name={row.nameOfInstitution} logoUpdatedAt={row.logoUpdatedAt} size="sm" />
          <div>
            <div className="font-medium text-gray-900 dark:text-white">{row.nameOfInstitution}</div>
            {row.shortNameOfInstitution && (
              <div className="text-sm text-gray-500 dark:text-gray-400">{row.shortNameOfInstitution}</div>
            )}
          </div>
        </div>
      )
    },
    {
      id: 'leaType',
      header: 'Type',
      accessor: 'leaType',
      sortable: true,
      searchable: true,
      width: '150px',
      render: (value) => {
        if (!value) return null;
        const colors: Record<string, string> = {
          'District': 'info',
          'CTC': 'success',
          'Jointure': 'warning',
          'Charter School': 'purple'
        };
        return (
          <Badge color={colors[value] || 'gray'} className="inline-flex uppercase">
            {value}
          </Badge>
        );
      }
    },
    {
      id: 'superintendentName',
      header: 'Superintendent',
      accessor: 'superintendentName',
      sortable: true,
      searchable: true,
      render: (value, row) => {
        if (value && row.superintendentId) {
          return (
            <Link
              to={`/persons/${row.superintendentId}`}
              onClick={(e) => e.stopPropagation()}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline text-left"
            >
              {value}
            </Link>
          );
        }
        return value || <span className="text-gray-400 dark:text-gray-500 italic">Not assigned</span>;
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: 'leaId',
      sortable: false,
      searchable: false,
      width: '100px',
      align: 'center',
      render: (value) => (
        <Link
          to={`/leas/${value}`}
          onClick={(e) => e.stopPropagation()}
          aria-label={`View details for organization ${value}`}
          className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg"
        >
          <HiEye className="mr-1 h-4 w-4" />
          View
        </Link>
      )
    }
  ], []);

  // Handle row click — supports Ctrl/Cmd-click and middle-click to open in a new tab
  const handleRowClick = (row: LEAListItem, e: React.MouseEvent) => {
    const url = `/leas/${row.leaId}`;
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(url);
  };

  // Handle Excel export
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await leasApi.export({ format: 'xlsx' });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `organizations_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to export organizations');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="container mx-auto p-8">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Local Education Agencies</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Browse and manage all educational organizations in the AIU directory
          </p>
        </div>

        <div className="flex gap-2">
          {isSuperAdmin && (
            <Button onClick={() => setShowCreateModal(true)}>
              <HiPlus className="mr-2 h-4 w-4" />
              Add Organization
            </Button>
          )}
          <Button
            color="gray"
            onClick={handleExport}
            disabled={leas.length === 0 || exporting}
          >
            <HiDownload className="mr-2 h-4 w-4" />
            {exporting ? 'Exporting...' : 'Export to Excel'}
          </Button>
        </div>
      </div>

      {error && (
        <Alert color="failure" className="mb-4" onDismiss={() => setError(null)}>
          <span className="font-medium">Error!</span> {error}
        </Alert>
      )}
      {successMessage && (
        <Alert color="success" className="mb-4" onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {loading ? (
        <Loading message="Loading organizations..." />
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <DataTable
            data={displayLeas}
            columns={columns}
            searchable={true}
            searchPlaceholder="Search by organization name, school name, type, or superintendent..."
            sortable={true}
            pagination={true}
            pageSize={25}
            pageSizeOptions={[10, 25, 50, 100]}
            selectable={false}
            onRowClick={handleRowClick}
            loading={loading}
            emptyMessage="No organizations found. The database is currently empty."
            hoverable={true}
            striped={false}
            caption="List of all Local Education Agencies"
            ariaLabel="Organizations table"
          />
        </div>
      )}

      {/* Create LEA Modal */}
      <CreateLEAModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={(leaId) => {
          setShowCreateModal(false);
          setSuccessMessage('Organization created successfully');
          setTimeout(() => setSuccessMessage(null), 5000);
          loadLEAs();
          navigate(`/leas/${leaId}`);
        }}
      />
    </div>
  );
};